import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CodeOutlined,
  FileSearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useVehicleCompanyData } from '../../../../../../../Hooks/TT_ELD/Vehicles';
import { camera } from '../../../../../../../API/TT_ELD/camera';

const { Text, Title } = Typography;

type TruckCameraProps = {
  vehicleId: string | number | undefined;
};

type CameraInfo = {
  id?: string | number;
  imei?: string | number;
  updatedAt?: string;
  lastUpdated?: string;
  lastSynced?: string;
  lastSync?: string;
  live?: boolean;
  isLive?: boolean;
  online?: boolean;
  isOnline?: boolean;
  vehicleId?: string | number;
  vehicle?: {
    id?: string | number;
    truck_number?: string | number;
  };
  driver?: {
    first_name?: string;
    second_name?: string;
  };
  [key: string]: any;
};

// Backend camera commands: commandType is the CAMERA_COMMANDS key. These ready commands take no
// parameters, so command is sent empty and the backend uses the command for that type.
// Only Set Voltage (accv,P1,P2,P3,P4) and Custom command send a command string.
const READY_COMMANDS = [
  {
    key: 'wakeup',
    label: 'Add wakeup',
    command: '',
    commandType: 'wakeup',
    icon: <BellOutlined rev={undefined} />,
  },
  {
    key: 'log',
    label: 'Send Logs - all logs to Camera Admin',
    command: '',
    commandType: 'log',
    icon: <FileSearchOutlined rev={undefined} />,
  },
  {
    key: 'status',
    label: 'Status',
    command: '',
    commandType: 'status',
    icon: <CodeOutlined rev={undefined} />,
  },
  {
    key: 'get-voltage',
    label: 'Get Voltage',
    command: '',
    commandType: 'voltage',
    icon: <ThunderboltOutlined rev={undefined} />,
  },
];

// Set Soft ACC Voltage Threshold: ACCV,P1,P2,P3,P4# — thresholds are in 0.1 V (280 = 28.0 V)
type VoltageSettings = { onVolts: number; onSeconds: number; offVolts: number; offSeconds: number };
const DEFAULT_VOLTAGE: VoltageSettings = { onVolts: 30, onSeconds: 3, offVolts: 12.8, offSeconds: 180 };
const toTenths = (volts: number) => Math.round(volts * 10);
const buildSetVoltageCommand = (v: VoltageSettings) =>
  `accv,${toTenths(v.onVolts)},${v.onSeconds},${toTenths(v.offVolts)},${v.offSeconds}`;

// Reply to "accv": ACCV,V:130,T:3,V2:128,T2:180 → current thresholds, to prefill Set Voltage
const parseVoltageReply = (reply?: string): VoltageSettings | null => {
  const match = reply?.match(/V:(\d+),T:(\d+),V2:(\d+),T2:(\d+)/i);
  if (!match) return null;
  return {
    onVolts: Number(match[1]) / 10,
    onSeconds: Number(match[2]),
    offVolts: Number(match[3]) / 10,
    offSeconds: Number(match[4]),
  };
};

const renderValue = (value?: React.ReactNode) => {
  if (value === undefined || value === null || value === '') return '-';
  return value;
};

const buildFullName = (person?: { first_name?: string; second_name?: string }) => {
  const fullName = [person?.first_name, person?.second_name].filter(Boolean).join(' ');
  return fullName || '-';
};

const findEmbeddedCamera = (vehicleData: any): CameraInfo | undefined => {
  return (
    vehicleData?.dvr ||
    vehicleData?.camera ||
    vehicleData?.dashcam ||
    vehicleData?.dashCam ||
    vehicleData?.cameraInfo ||
    vehicleData?.dvrInfo
  );
};

const getDvrId = (vehicleData: any, cameraInfo?: CameraInfo) => {
  return (
    cameraInfo?.dvrId ||
    cameraInfo?.id ||
    cameraInfo?.dvr?.id ||
    vehicleData?.dvrId ||
    vehicleData?.dvr?.id ||
    vehicleData?.cameraId ||
    vehicleData?.camera?.id ||
    vehicleData?.dashcam?.id ||
    vehicleData?.dashCam?.id
  );
};

const TruckCamera: React.FC<TruckCameraProps> = ({ vehicleId }) => {
  const { data: vehicleData, isLoading, isFetching } = useVehicleCompanyData(vehicleId);
  const [companyCameras, setCompanyCameras] = useState<CameraInfo[]>([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState<string>('');
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customCommand, setCustomCommand] = useState('');
  const [voltageModalOpen, setVoltageModalOpen] = useState(false);
  const [voltageDefaults, setVoltageDefaults] = useState<VoltageSettings>(DEFAULT_VOLTAGE);
  const [lastReply, setLastReply] = useState<{ label: string; text: string } | null>(null);
  const [voltageForm] = Form.useForm<VoltageSettings>();
  const voltagePreview = Form.useWatch([], voltageForm) as VoltageSettings | undefined;

  useEffect(() => {
    const embeddedCamera = findEmbeddedCamera(vehicleData);
    const companyId = vehicleData?.companyId || vehicleData?.company?.id;

    if (!vehicleData || embeddedCamera || !companyId) return;

    let isMounted = true;
    setCameraLoading(true);

    camera
      .read(true, companyId)
      .then((res) => {
        if (!isMounted) return;
        setCompanyCameras(res?.data || []);
      })
      .finally(() => {
        if (isMounted) setCameraLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [vehicleData]);

  const cameraInfo = useMemo(() => {
    const embeddedCamera = findEmbeddedCamera(vehicleData);
    if (embeddedCamera) return embeddedCamera;

    const currentVehicleId = String(vehicleData?.id || vehicleId || '');
    return companyCameras.find((item) => {
      const cameraVehicleId = item?.vehicleId || item?.vehicle?.id;
      return cameraVehicleId && String(cameraVehicleId) === currentVehicleId;
    });
  }, [companyCameras, vehicleData, vehicleId]);

  const liveValue =
    cameraInfo?.live ?? cameraInfo?.isLive ?? cameraInfo?.online ?? cameraInfo?.isOnline ?? null;

  // Sends one command; `key` identifies the button for its loading state. Returns the device's
  // reply text (e.g. "wakeup!" or "ACCV,V:130,T:3,V2:128,T2:180") when the command succeeds.
  const sendCommand = async (
    key: string,
    command: string,
    commandType: string,
  ): Promise<string | undefined> => {
    const dvrId = getDvrId(vehicleData, cameraInfo);

    if (!dvrId) {
      message.error('DVR ID not found for this truck camera');
      return;
    }

    setCommandLoading(key);
    try {
      const response = await camera.sendCommandToCamera({
        command,
        commandType,
        dvrId,
        vehicleId: vehicleData?.id || vehicleId,
        companyId: vehicleData?.companyId || vehicleData?.company?.id,
      });
      const reply: string | undefined = response?.data?._content;
      setLastReply(reply ? { label: key, text: reply } : null);
      return reply;
    } catch {
      // the API layer already shows the error
      return undefined;
    } finally {
      setCommandLoading('');
    }
  };

  const handleCustomCommand = async () => {
    const command = customCommand.trim();
    if (!command) return;

    await sendCommand('custom', command, 'custom');
    setCustomModalOpen(false);
    setCustomCommand('');
  };

  const handleReadyCommand = async (item: (typeof READY_COMMANDS)[number]) => {
    const reply = await sendCommand(item.key, item.command, item.commandType);
    // Keep the camera's current thresholds so Set Voltage opens with them
    const current = item.key === 'get-voltage' ? parseVoltageReply(reply) : null;
    if (current) setVoltageDefaults(current);
  };

  const openVoltageModal = () => {
    voltageForm.setFieldsValue(voltageDefaults);
    setVoltageModalOpen(true);
  };

  const handleSetVoltage = async () => {
    const values: VoltageSettings = await voltageForm.validateFields();
    const reply = await sendCommand('set-voltage', buildSetVoltageCommand(values), 'voltage');
    if (reply !== undefined) {
      setVoltageDefaults(values);
      setVoltageModalOpen(false);
    }
  };

  return (
    <Spin spinning={isLoading || isFetching || cameraLoading}>
      <Card style={{ marginTop: 20 }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Truck Camera
            </Title>
            <Text type="secondary">Send dashcam commands with one click.</Text>
          </div>

          <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 3 }}>
            <Descriptions.Item label="IMEI">{renderValue(cameraInfo?.imei)}</Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {renderValue(cameraInfo?.lastUpdated || cameraInfo?.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Current Driver">
              {buildFullName(cameraInfo?.driver || vehicleData?.driver)}
            </Descriptions.Item>
            <Descriptions.Item label="Current Truck Number">
              {renderValue(vehicleData?.truck_number || cameraInfo?.vehicle?.truck_number)}
            </Descriptions.Item>
            <Descriptions.Item label="Last Synced">
              {renderValue(cameraInfo?.lastSynced || cameraInfo?.lastSync)}
            </Descriptions.Item>
            <Descriptions.Item label="Live">
              {liveValue === null ? (
                '-'
              ) : (
                <Tag color={liveValue ? 'green' : 'red'}>{liveValue ? 'Live' : 'Offline'}</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          {!cameraInfo && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No assigned camera data found for this truck"
            />
          )}

          <div>
            <Title level={5}>Ready commands</Title>
            <Row gutter={[12, 12]}>
              {READY_COMMANDS.map((item) => (
                <Col xs={24} sm={12} md={8} lg={6} xl={5} key={item.key}>
                  <Button
                    block
                    type="primary"
                    icon={item.icon}
                    loading={commandLoading === item.key}
                    disabled={Boolean(commandLoading)}
                    onClick={() => handleReadyCommand(item)}
                  >
                    {item.label}
                  </Button>
                </Col>
              ))}
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                <Button
                  block
                  type="primary"
                  icon={<ThunderboltOutlined rev={undefined} />}
                  loading={commandLoading === 'set-voltage'}
                  disabled={Boolean(commandLoading)}
                  onClick={openVoltageModal}
                >
                  Set Voltage
                </Button>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                <Button
                  block
                  icon={<CodeOutlined rev={undefined} />}
                  disabled={Boolean(commandLoading)}
                  onClick={() => setCustomModalOpen(true)}
                >
                  Custom command
                </Button>
              </Col>
            </Row>
            {lastReply && (
              <Alert
                style={{ marginTop: 12 }}
                type="success"
                showIcon
                closable
                onClose={() => setLastReply(null)}
                message="Camera reply"
                description={<Text code>{lastReply.text}</Text>}
              />
            )}
          </div>
        </Space>
      </Card>

      <Modal
        title="Send custom camera command"
        open={customModalOpen}
        onCancel={() => {
          setCustomModalOpen(false);
          setCustomCommand('');
        }}
        onOk={handleCustomCommand}
        confirmLoading={Boolean(commandLoading)}
        okButtonProps={{ disabled: !customCommand.trim() }}
        okText="Send"
      >
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 6 }}
          placeholder="Enter command"
          value={customCommand}
          onChange={(event) => setCustomCommand(event.target.value)}
        />
      </Modal>

      <Modal
        title="Set Soft ACC Voltage Threshold"
        open={voltageModalOpen}
        onCancel={() => setVoltageModalOpen(false)}
        onOk={handleSetVoltage}
        confirmLoading={commandLoading === 'set-voltage'}
        okText="Set voltage"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Set the soft ACC parameters (ACC command) before using this command."
        />
        <Form form={voltageForm} layout="vertical" initialValues={voltageDefaults} preserve={false}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ACC ON voltage threshold"
                name="onVolts"
                tooltip="ACC turns ON when the voltage is at or above this value for the ON detection time"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={9} max={36} step={0.1} precision={1} suffix="V" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ACC ON detection time"
                name="onSeconds"
                tooltip="Checked every second for this long before ACC turns ON"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={1} max={300} precision={0} suffix="sec" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ACC OFF voltage threshold"
                name="offVolts"
                tooltip="ACC turns OFF when the voltage is below this value for the OFF detection time"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={9} max={36} step={0.1} precision={1} suffix="V" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="ACC OFF detection time"
                name="offSeconds"
                tooltip="Checked every second for this long before ACC turns OFF"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={1} max={300} precision={0} suffix="sec" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Text type="secondary">
          Command:{' '}
          <Text code>
            {voltagePreview &&
            [voltagePreview.onVolts, voltagePreview.onSeconds, voltagePreview.offVolts, voltagePreview.offSeconds].every(
              (value) => typeof value === 'number',
            )
              ? `${buildSetVoltageCommand(voltagePreview)}`.toUpperCase() + '#'
              : '-'}
          </Text>
        </Text>
      </Modal>
    </Spin>
  );
};

export default TruckCamera;
